import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { finalize, map, Observable } from 'rxjs';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OrganizationUserDto } from 'src/app/data-transfer/entities/organization-user-dto';
import { OrganizationUserGroupDto } from 'src/app/data-transfer/entities/user-group-entities/organization-user-group-dto';
import { DialogActions } from 'src/app/model/dictionary';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { AdminDialogResult } from '../admin-dialog-result';
import { getApiErrorMessage } from '../admin-api-error';
import { AdminUserRole } from '../organization-user.util';

export interface AdminUserDialogData {
  mode: 'create' | 'edit';
  user?: OrganizationUserDto;
  role?: AdminUserRole;
  allGroups: OrganizationUserGroupDto[];
  groupIds?: string[];
  existingEmails: Set<string>;
  isLeadAdmin: boolean;
  premiumLimitReached: boolean;
  passwordLoginDisabled: boolean;
  emailLocked: boolean;
  nameLocked?: boolean;
  isSelf?: boolean;
  /** Saves the new user; the dialog stays open until it succeeds. */
  save?: (data: AdminUserDialogResultData) => Observable<unknown>;
}

export interface AdminUserDialogResultData {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  groupIds: string[];
  isPremiumUser: boolean;
  role: AdminUserRole;
}

export type AdminUserDialogResult = AdminDialogResult<AdminUserDialogResultData>;

const emailUniqueValidator = (existingEmails: Set<string>): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
  const value = (control.value as string | null)?.toLowerCase();
  return value && existingEmails.has(value) ? { emailAlreadyUsed: true } : null;
};

@Component({
  selector: 'app-admin-user-dialog',
  templateUrl: './admin-user-dialog.component.html',
  styleUrls: ['./admin-user-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminUserDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<AdminUserDialogComponent, AdminUserDialogResult>>(MatDialogRef);
  protected readonly data = inject<AdminUserDialogData>(MAT_DIALOG_DATA);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isSaving = signal(false);
  protected readonly form = this.buildForm();
  protected submitted = false;
  protected readonly isPremiumUser = signal(this.data.user?.isPremiumUser ?? false);
  protected readonly role = signal<AdminUserRole>(this.data.role ?? AdminUserRole.Member);
  protected readonly selectedGroupIds = signal<ReadonlySet<string>>(new Set(this.data.groupIds ?? []));
  protected readonly AdminUserRole = AdminUserRole;

  private readonly formValue = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), { initialValue: this.form.getRawValue() });

  protected readonly hasChanges = computed(() => {
    if (!this.isEditing) return true;
    const user = this.data.user;
    const { firstName, lastName, email } = this.formValue();
    const initialGroupIds = new Set(this.data.groupIds ?? []);
    const groupIds = this.selectedGroupIds();
    return (
      firstName !== (user?.firstName ?? '') ||
      lastName !== (user?.lastName ?? '') ||
      email !== (user?.email ?? '') ||
      this.isPremiumUser() !== (user?.isPremiumUser ?? false) ||
      this.role() !== (this.data.role ?? AdminUserRole.Member) ||
      groupIds.size !== initialGroupIds.size ||
      [...groupIds].some(id => !initialGroupIds.has(id))
    );
  });

  protected readonly hasRestrictedGroup = computed(() =>
    this.data.allGroups.some(group => group.isDirectoryPermissionsEnabled && this.selectedGroupIds().has(group.id))
  );
  protected readonly isSelfLeadAdmin = computed(() => this.isEditing && this.role() === AdminUserRole.Lead);
  protected readonly isAdminToggleLocked = computed(() => !this.data.isLeadAdmin || this.isSelfLeadAdmin());
  protected readonly isPremiumBlocked = computed(() => this.data.premiumLimitReached && !this.isPremiumUser());

  protected get isEditing(): boolean {
    return this.data.mode === 'edit';
  }

  protected get needsPasswordField(): boolean {
    return this.data.mode === 'create' && !this.data.passwordLoginDisabled;
  }

  protected setGroupSelected(group: OrganizationUserGroupDto, isSelected: boolean): void {
    if (this.selectedGroupIds().has(group.id) === isSelected) return;
    const groupIds = new Set(this.selectedGroupIds());
    if (isSelected) {
      groupIds.add(group.id);
    } else {
      groupIds.delete(group.id);
    }
    this.selectedGroupIds.set(groupIds);
  }

  protected toggleAdmin(isAdmin: boolean): void {
    if (this.isAdminToggleLocked()) return;
    this.role.set(isAdmin ? AdminUserRole.Admin : AdminUserRole.Member);
  }

  protected save(): void {
    if (!this.hasChanges()) return;
    if (this.form.invalid) {
      this.submitted = true;
      this.form.markAllAsTouched();
      return;
    }

    const { firstName, lastName, email, password } = this.form.getRawValue();
    const result: AdminUserDialogResult = {
      event: DialogActions.CONFIRM,
      data: {
        firstName,
        lastName,
        email,
        password: this.needsPasswordField ? password?.password : undefined,
        groupIds: [...this.selectedGroupIds()],
        isPremiumUser: this.isPremiumUser(),
        role: this.role(),
      },
    };
    if (!this.data.save) {
      this.dialogRef.close(result);
      return;
    }

    this.isSaving.set(true);
    this.data
      .save(result.data)
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.dialogRef.close(result),
        error: (error: HttpErrorResponse) => this.snackbarService.showError(getApiErrorMessage(error)),
      });
  }

  protected closeDialog(): void {
    if (this.isSaving()) return;
    this.dialogRef.close({ event: DialogActions.REJECT });
  }

  private buildForm() {
    const form = new FormGroup({
      firstName: new FormControl(this.data.user?.firstName ?? '', { nonNullable: true, validators: [Validators.required] }),
      lastName: new FormControl(this.data.user?.lastName ?? '', { nonNullable: true, validators: [Validators.required] }),
      email: new FormControl(this.data.user?.email ?? '', {
        nonNullable: true,
        validators: [Validators.required, Validators.email, emailUniqueValidator(this.data.existingEmails)],
      }),
      password: new FormControl<{ password: string; confirmPassword: string } | null>(null, Validators.required),
    });

    if (!this.needsPasswordField) {
      form.controls.password.disable();
    }
    if (this.data.mode === 'edit' && this.data.emailLocked) {
      form.controls.email.disable();
    }
    if (this.data.mode === 'edit' && this.data.nameLocked) {
      form.controls.firstName.disable();
      form.controls.lastName.disable();
    }
    return form;
  }
}
